import {
  type FC, useEffect, useMemo, useState,
} from 'react';
import { FontAwesomeIcon } from '~/_shared/baseComponents/icon/fontAwesomeIcon.component';
import { Stack } from '~/_shared/baseComponents/layout/stack.component';
import {
  TooltipComponent, TooltipPlacement,
} from '~/_shared/baseComponents/tooltip/tooltip.component';
import { MapOverlayPanelComponent } from '~/_shared/components/mapOverlay';
import { usePrevious } from '~/_shared/utils/hooks/usePrevious';
import {
  contentStyle, mapOverlayPanelStyle, overlappingPanelTabStyle, overlappingPanelTabsWrapperStyle,
} from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.styles';
import type { OverlappingUIPanelTabs } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUI.types';
import { OverlappingUIPanelItemComponent } from '~/map/mapToolControls/menuItems/overlappingUI/overlappingUIPanelItem.component';

export type OverlappingUIPanelProps = Readonly<{
  overlappingTabs: readonly OverlappingUIPanelTabs[];
}>;

export const OverlappingUIPanelComponent: FC<OverlappingUIPanelProps> = ({ overlappingTabs }) => {
  const relevantTabs = useMemo(() => {
    return overlappingTabs.filter((tab) => (tab.elements.length > 1));
  }, [overlappingTabs]);
  const previousRelevantTabs = usePrevious(relevantTabs);
  const [activeTabId, setActiveTabId] = useState(relevantTabs[0]?.id);
  const activeTab = relevantTabs.find((tab) => tab.id === activeTabId);

  useEffect(() => {
    if (!activeTab) {
      setActiveTabId(relevantTabs[0]?.id);
    }
  }, [activeTab, relevantTabs]);

  useEffect(() => {
    const oldTabIDs = previousRelevantTabs?.map((tab) => tab.id) ?? [];
    const newTabIDs = relevantTabs.map(tab => tab.id);

    const newlyAddedTabId = newTabIDs.filter(id => !oldTabIDs.includes(id))?.[0];
    if (newlyAddedTabId) {
      setActiveTabId(newlyAddedTabId);
    }
  }, [relevantTabs, previousRelevantTabs]);

  const overlappingElements = activeTab?.elements;

  useEffect(() => {
    const _ = overlappingElements; // To ensure dependency
    // updateScrollBoundariesRef?.current();
  }, [overlappingElements]);

  if (!overlappingElements || (overlappingElements.length < 2)) {
    return null;
  }

  return (
    <MapOverlayPanelComponent
      css={mapOverlayPanelStyle}
      prefix={(relevantTabs.length > 1) && (
        <div
          data-element="overlapping-panel-tabs-wrapper"
          css={overlappingPanelTabsWrapperStyle}
        >
          {relevantTabs.map((tab) => (
            <TooltipComponent
              key={tab.id}
              tooltipContent={tab.tooltipText}
              placement={TooltipPlacement.Right}
            >
              <button
                data-is-active={tab.id === activeTabId}
                css={overlappingPanelTabStyle}
                onClick={() => setActiveTabId(tab.id)}
              >
                <FontAwesomeIcon icon={tab.icon} />
              </button>
            </TooltipComponent>
          ))}
        </div>
      )}
      content={(
        <Stack gap={8} css={contentStyle}>
          {overlappingElements.map((item, index) => (
            <OverlappingUIPanelItemComponent
              key={`${index}|${item.name}`}
              item={item}
            />
          ))}
        </Stack>
      )}
    />
  );
};
